import {
  FloatingFocusManager,
  FloatingPortal,
  autoUpdate,
  useFloating,
} from '@floating-ui/react';
import { AnimatePresence, motion } from 'framer-motion';
import hexToRgba from 'hex-to-rgba';
import React, { useCallback } from 'react';
import styled from 'styled-components';
import IconClose from '@/assets/icons/icon-close.svg';
import useScrollLock from '@/hooks/useScrollLock';
import { dialogCardVariant, dialogDimVariant } from '@/styles/motion';
import { DialogPanel } from './atom/DialogPanel';
import { FlexBox } from './atom/FlexBox';

export interface Props extends React.PropsWithChildren {
  className?: string;
  isShow?: boolean;
  isNoCloseButton?: boolean;
  onDimClick?: () => void;
  onCloseClick?: () => void;
}

const Dim = styled(FlexBox)`
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background: ${(p) => hexToRgba(p.theme.colors.black, 0.4)};
  backdrop-filter: blur(12px);
  outline: none;
  z-index: 1;
`;
const CloseButton = styled.button`
  position: absolute;
  top: ${(p) => p.theme.size['xl']};
  right: ${(p) => p.theme.size['xl']};
`;

export default function Dialog({
  className,
  children,
  isShow,
  isNoCloseButton,
  onDimClick: handleDimClick,
  onCloseClick: handleCloseClick,
}: Props) {
  useScrollLock({
    isEnable: !isShow,
  });

  const { refs, context } = useFloating({
    strategy: 'fixed',
    open: isShow,
    whileElementsMounted: autoUpdate,
  });

  const handlePanelClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  return (
    <AnimatePresence initial={false}>
      {isShow ? (
        <FloatingPortal>
          <FloatingFocusManager context={context}>
            <Dim
              $flex
              $align="center"
              $justify="center"
              initial="hidden"
              animate="show"
              exit="exit"
              as={motion.div}
              variants={dialogDimVariant}
              ref={refs.setFloating}
              onClick={handleDimClick}
            >
              <DialogPanel
                className={className}
                $padding="xl"
                $rounded="xl"
                initial="hidden"
                animate="show"
                exit="exit"
                as={motion.div}
                variants={dialogCardVariant}
                onClick={handlePanelClick}
              >
                {children}
                {!isNoCloseButton && (
                  <CloseButton onClick={handleCloseClick}>
                    <IconClose />
                  </CloseButton>
                )}
              </DialogPanel>
            </Dim>
          </FloatingFocusManager>
        </FloatingPortal>
      ) : null}
    </AnimatePresence>
  );
}
